import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Form, FormLayout, Card, Layout, Select, Stack, SettingToggle, TextStyle, Caption, Checkbox, Button, Modal, Thumbnail, Toast,
  ProgressBar, ButtonGroup, InlineError, Banner } from '@shopify/polaris';

import { BasicTextField, FileDropZone, TagsInput } from '@presentational';

import ProductFormVariants from './ProductFormVariants';
import VariantModal from './VariantModal';
import APBChangeModal from './APBChangeModal';

import FormPageActions from '@container/FormPageActions';
import ImagePreview from '@presentational/ImagePreview';
import ImageStackItem from '@presentational/ImageStackItem';

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { tAttribute } from '@utils';

import CkeditorInput from '../CkeditorInput';

import { defaultProductOption } from '@utils/recordHelpers';
import { values } from 'lodash';

const propTypes = {
  warranties: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.object,

  update: PropTypes.bool,
  hasDefaultVariant: PropTypes.bool,
  status: PropTypes.object, // formik status
  variantsURL: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  locationName: PropTypes.string.isRequired,

  closeApbConfirmation: PropTypes.func.isRequired,
  openApbConfirmation: PropTypes.func.isRequired,
  displayApbConfirmation: PropTypes.bool.isRequired,
  approvalStatus: PropTypes.string,

  createMixedImageList: PropTypes.func.isRequired,
};

const defaultProps = {
  variantsURL: "new_product"
}

class ProductForm extends Component {
  constructor(props) {
    super(props);
  }

  dropZonePlaceholder = () => {
    return (
      <div
        className="flex justifyCenter alignItemsCenter flexColumn" >
        <img
          alt="File"
          className="mb10"
          src="https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304"
        />
        <Caption>
          Upload or drop files
        </Caption>
      </div>
    )
  }

  toggleVariants = (checked) => {
    const { toggleValue, setFieldValue, values } = this.props;
    const { default_variant_data } = values;
    let variants = values.variants;

    toggleValue("variantsEnabled");

    // Special case when a Product has only default variant (no variants) and is changing to variants
    if(!!default_variant_data && default_variant_data.variant.id){
      if(checked){
        setFieldValue("options", [defaultProductOption]);
        setFieldValue("variants", []); // Remove default variant
        setFieldValue("changingToVariants", true);
      }
      else{
        setFieldValue("options", [default_variant_data.option]);
        setFieldValue("variants", [default_variant_data.variant]);
        setFieldValue("changingToVariants", false);
      }
    }
    // Normal case.
    else{
      if (checked && (!Array.isArray(variants) || !variants.length)) { // array does not exist, is not an array, or is empty
        // Toggling on. Initialize with default variant.
        setFieldValue("options", [defaultProductOption]);
      }
      else{
        // Toggling off. Placeholder if any extra logic should be set here
        setFieldValue("options", []);
        setFieldValue("variants", []);
      }
    }
  }

  handleChangeEditor = (value) => {
    this.props.handleChange(value, 'description_html')
  }

  getFileURL = (file) => {
    if(file.url){
      return file.url
    }
    else{
      const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];
      return validImageTypes.indexOf(file.type) > 0 ? window.URL.createObjectURL(file) : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
    }
  }

  renderImageStackItem = (imageData, index) => {
    if(imageData.id){
      return(
        <ImageStackItem 
          key={ index }
          url={ imageData.url }
          filename={ imageData.altText }
          size={ null }
          onClick={ () => this.props.openImagePreview(imageData.url) }
          onDelete={ () => this.props.removeRemoteImage(imageData.id, index)  }
        />
      );
    } else {
      const fileURL = this.getFileURL(imageData);
      return(
        <ImageStackItem 
          key={index}
          url={ fileURL }
          filename={ imageData.name }
          size={ imageData.size }
          onClick={ () => this.props.openImagePreview(fileURL) }
          onDelete={ () => this.props.onImageDelete(imageData.uniqTempName, index) }
        />
      );
    }
  }

  render() {
    let props = this.props;
    let { handleChange, values, errors, readOnly, defaultWarrantyId } = this.props;

    let basicFieldProps = {
      handleChange,
      values,
      errors,
      readOnly,
    }

    // If on create always show all fields. Else on update only display the (price, and variant fields) IF product only has the default variant, else those fields goes to the variant responsability.
    // TODO Need to check the case of pending product, when not synced never (missing sp_id) and when already synced but pending.
    const writeOnDefaultVariant = (!props.update || props.hasDefaultVariant); 

    return (
      <div>
        { props.status.showToast ?
          <Toast 
            content={props.status.toastContent} 
            onDismiss={props.closeToast} />
          : null
        }

        {/* values.status is used for the initial value. approvalStatus is for the cases in which the status gets updated (approval/denial) */}
        { Boolean(props.progress) &&
          <div className="mb10">
            <Card sectioned>
              <div className={`mb20 statusProgressContainer ${props.approvalStatus || values.status}`}>
                <div className="statusLabel">
                  { I18n.t("app.current_status") } { I18n.t(`enum.status.${props.approvalStatus || values.status}`) }
                </div>

                <ProgressBar progress={props.progress} />

                {/* Little description to diff between pending update and pending first publish */}
                { !!values.status_description &&
                  <Banner
                    status="info"
                  >
                    <div className="flex justifyCenter">
                      <TextStyle variation="strong">
                        { values.status_description }
                      </TextStyle>
                    </div>
                  </Banner>
                }
  
                { props.nextAction &&
                  <div className="mt10">
                    <ButtonGroup fullWidth>
                      <Button loading={props.loading} 
                        onClick={ props.onReject } 
                        size="slim" destructive>
                        { I18n.t("actions.reject") } 
                      </Button>
                        <Button loading={props.loading}
                          onClick={ props.onApprove } 
                          size="slim" primary>
                          { props.nextAction }
                        </Button>
                    </ButtonGroup>
                  </div>
                }
              </div>
              { errors.base &&
                <InlineError message={ errors.base } />
              }
            </Card>
          </div>
        }
  
        <Form onSubmit={ props.handleSubmit }>
          <Layout>

            {/* >> LEFT Side of Layout << */}
            <Layout.Section>
  
              <Card sectioned>
                <FormLayout>
                  <BasicTextField 
                    name="title"
                    label={ tAttribute("product", "title") }
                    {...basicFieldProps}
                  />
                  <CkeditorInput
                    label={tAttribute("product", "description_html")}
                    name="description_html"
                    value={ values.description_html }
                    onChange={ props.handleChange }
                  />
                </FormLayout>
              </Card>

              {/* ========================= Images section =========================*/}
              <ImagePreview 
                open={ !!props.previewImageSrc }
                src={ props.previewImageSrc }
                onClose={ props.closeImagePreview }
              />

              {/* Images */}
              <Card
                title={ tAttribute("product", "images") } 
                >
                { (values.images.length > 0 || values.local_images.length > 0) &&
                  <Card.Section>
                    
                    <DragDropContext onDragEnd={this.props.onImageDrag}>
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            //style={getListStyle(snapshot.isDraggingOver)}
                          >

                            <Stack vertical>
                              { this.props.createMixedImageList().map( (imageData, index) => (
                                <Draggable key={`drag-${index}`} draggableId={`drag-${index}`} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      {this.renderImageStackItem(imageData, index)}
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Stack>

                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>

                  </Card.Section>
                }

                {/* local_images */}
                <Card.Section>
                  <FileDropZone 
                    title={tAttribute("product", "local_images")}
                    actionTitle={I18n.t("actions.upload")}
                    files={ values.local_images }
                    onDrop={ this.props.onImageUpload }
                    uploadHelp={ this.dropZonePlaceholder }
                    readOnly={ readOnly }
                    error={ errors && errors.local_images }
  
                    dropZoneProps={{ accept: "image/jpg,image/jpeg,image/png" }}
                  />
                  <p className="mt10">
                    { I18n.t("form.hint.product.images") }
                  </p>
                </Card.Section>
              </Card>
  

              {/* ========================= Images section =========================*/}

              {/* Inventory Section */}
              <Card title={ I18n.t("shopify.inventory") }>

                <Card.Section>
                  <FormLayout>
                    { writeOnDefaultVariant && !values.variantsEnabled &&
                      <FormLayout.Group>
                        <BasicTextField
                          name="sku"
                          label={tAttribute("product", "sku")}
                          {...basicFieldProps}
                        />
                        <BasicTextField
                          name="barcode"
                          label={tAttribute("product", "barcode")}
                          {...basicFieldProps}
                        />
                      </FormLayout.Group>
                    }
                    <BasicTextField
                      name="cabys"
                      label={ tAttribute("product", "cabys") }
                      maxLength={13}
                      {...basicFieldProps}
                    />
                  </FormLayout>
                </Card.Section>
  
                {/* APB ONLY FOR BARUUUULUUU | This is now deprecated. APU value is set on seller instead, toggling on form is now removed */}
                {/* <SettingToggle
                  action={{
                    content: values.apb ? I18n.t("actions.disable") : I18n.t("actions.enable"),
                    onAction: props.openApbConfirmation,
                  }}
                  enabled={values.apb}
                >
                  { I18n.t("app.apb.title") }
                  <br/>
                  <TextStyle variation="strong">
                    { values.apb ? I18n.t("app.apb.enabled") : I18n.t("app.apb.disabled") }
                  </TextStyle>
                </SettingToggle> */}

                {/* <APBChangeModal 
                  open={ props.displayApbConfirmation }
                  onClose={ props.closeApbConfirmation }
                  disabled={ !props.canToggleAPB }

                  currentAPB={ values.apb }
                  onAction={ readOnly ? () => true : () => { props.toggleValue("apb"); props.closeApbConfirmation() } }
                /> */}

                {/* Display a small informative block to easily tell when a Product is APU. THIS doesn't appear on non-apu products */}
                { values.apb &&
                  <Card.Section>
                    { I18n.t("app.apb.title") }
                    <br/>
                    <TextStyle variation="strong">
                    { I18n.t("app.apb.enabled") }
                    </TextStyle>
                  </Card.Section>
                }

                {/*===================  APB *============== */}
                  
                {/* Here the label should display the DefaultLocation for seller. */}
                { !values.apb && writeOnDefaultVariant &&
                  <Card.Section title={ I18n.t("app.quantity") }>
                    <FormLayout>
                      <Stack alignment="center" >
                        <Stack.Item fill>
                          { props.locationName }
                        </Stack.Item>
                        <BasicTextField
                          name="inventory_quantity"
                          type="number"
                          labelHidden
                          align="right"
                          label={tAttribute("product", "inventory_quantity")}
                          {...basicFieldProps}
                        />
                      </Stack>
                    </FormLayout>
                  </Card.Section>
                }
              </Card>
            
              {/* Variants section */}
              <Card title={I18n.t("shopify.variants")}>
                <VariantModal
                    apb={ values.apb }
                    open={ props.editingVariant }
                    variantIndex={ props.variantIndex || 0 }
                    onClose={ props.closeVariantModal }
                    onAction={ props.saveVariantChanges }
                    fieldProps={ basicFieldProps }
                    variants={ values.variants }
                    variantsURL={ props.variantsURL }

                    isAdmin={ props.isAdmin }
                  />

                {/* Product with varians cannot be deselected this way */}
                { writeOnDefaultVariant ?
                  <Card.Section>
                    <Checkbox
                      disabled={ props.readOnly }
                      checked={values.variantsEnabled}
                      label={ I18n.t("shopify.toggle_variants") }
                      onChange={ this.toggleVariants }
                    />
                  </Card.Section>
                  :
                  // Add variant link when editing a product with variants(cannot directly add via options)
                  <Card.Section>
                    <Button plain url={`${props.variantsURL}/new`}>
                      { I18n.t("actions.add_x", { x: I18n.t("activerecord.models.variant.one") }) }
                    </Button>
                  </Card.Section>
                }
                  
                {/* Product already with variants can't directly edit Options */}
                {values.variantsEnabled &&
                  <ProductFormVariants
                    canEditOptions={ (writeOnDefaultVariant) }

                    readOnly={ readOnly }
                    variants={ values.variants }
                    options={ values.options }
                    experimental={ basicFieldProps }
                    handleChange={ handleChange }
                    handleOptionValuesChange={ props.handleOptionValuesChange }
                    reloadVariants = { props.reloadVariants }
                    variantTypes={ props.variantTypes }
                    onVariantClick={ props.onVariantClick }
                    apb={ values.apb }
                    errors={ errors }
                  />
                }
  
                
  
              </Card>
  
            </Layout.Section>
  
  
            {/* RIGHT SECTION LAYOUT */}
            <Layout.Section secondary>
              {/* PRICING SECTION */}
              { writeOnDefaultVariant &&
                <Card title={ I18n.t("shopify.pricing") } sectioned>
                  <FormLayout>
                    <BasicTextField
                      name="barulu_cost"
                      type="number"
                      label={tAttribute("product", "barulu_cost")}
                      {...basicFieldProps}
                    />
    
                    <BasicTextField
                      name="publish_price"
                      type="number"
                      label={tAttribute("product", "publish_price")}
                      {...basicFieldProps}
                      readOnly={ !props.isAdmin }
                    />
                  </FormLayout>
                </Card>
              }
                
              {/* ORGANIZATION SECTION */}
              <Card title={ I18n.t("shopify.organization") } sectioned>
                <FormLayout>
                  <BasicTextField
                    name="product_type"
                    label={tAttribute("product", "product_type")}
                    {...basicFieldProps}
                  />
    
                  <Select 
                    id="warranty_id"
                    name="warranty_id"
                    label={ tAttribute("product", "warranty_id") }
                    options={ props.warranties }
                    onChange={ this.props.handleChangeNumber }
                    value={ values.warranty_id }
                    disabled={ readOnly }
                    error={ errors.warranty }
                  />

                  { writeOnDefaultVariant &&
                    <BasicTextField
                      name="weight"
                      type="number"
                      label={tAttribute("variant", "weight")}
                      {...basicFieldProps}
                      connectedRight={
                        <Select 
                          id="weight_unit"
                          onChange={handleChange}
                          value={ values.weight_unit }
                          disabled={ readOnly }
                          label="Weight unit" labelHidden options={ props.weightUnits } />
                      }
                    />
                  }
                </FormLayout>
              </Card>
            </Layout.Section>
  
          </Layout>
          <FormPageActions
            loading={ props.status.submitLoading }
            onPrimaryAction={ props.handleSubmit }
            deleteURL={ props.deleteURL }
          />

        </Form>
      </div>
    );
  }
}


ProductForm.propTypes = propTypes;
ProductForm.defaultProps = defaultProps;


export default ProductForm;
